import React, { createRef, useRef } from "react"
import "./index.css"

interface OptionalProps {
    allowResize?: boolean
    minimumWidth?: number
    dividerWidth?: number
    classNameContainer?: string
    classNameLeftPanel?: string
    classNameRightPanel?: string
    classNameDivider?: string
}

interface Props extends OptionalProps {
    panelLeft: React.ReactNode
    panelRight: React.ReactNode
}

interface State {
    widthLeft: string
    widthRight: string
    dragging: boolean
}

const DEFAULT_VALS = {
    allowResize: true,
    minimumWidth: 250,
    dividerWidth: 4,
    classNameContainer: "",
    classNameLeftPanel: "",
    classNameRightPanel: "",
    classNameDivider: "",
}

export default class SplitPanel extends React.Component<Props, State> {
    static defaultProps: Partial<Props> = {
        allowResize: true,
        minimumWidth: 250,
        dividerWidth: 4,
        classNameContainer: "",
        classNameLeftPanel: "",
        classNameRightPanel: "",
        classNameDivider: "",
    }

    containerRef = createRef<HTMLDivElement>()

    constructor(props: Props) {
        super(props)
        this.state = {
            dragging: false,
            widthLeft: "",
             widthRight: "",
        }
    }

    componentDidMount(): void {
        // Add various event handlers (and remove them when done!)
        window.addEventListener("mousemove", this.handleMouseMove)
        window.addEventListener("mouseup", this.handleMouseUp)
        window.addEventListener("resize", this.handleResize)

        this.adjustPanels()
    }

    componentWillUnmount() {
        // Remove the event handlers we added!
        window.removeEventListener("mousemove", this.handleMouseMove)
        window.removeEventListener("mouseup", this.handleMouseUp)
        window.removeEventListener("resize", this.handleResize)
    }

    adjustPanels = () => {
        let {minimumWidth} = this.props
        let rect = this.containerRef.current?.getBoundingClientRect()
        let initialWidth = rect ? `${Math.floor(rect.width/2)}px` : `${minimumWidth}px`
        this.setState({ widthLeft: initialWidth, widthRight: initialWidth })
    }

    handleResize = (_e) => {
        this.adjustPanels()
    }

    handleMouseDown = (_e) => {
        this.setState({dragging: true})
    }

    handleMouseUp = (_e) => {
        this.setState({dragging: false})
    }

    handleMouseMove = (e) => {
        let {dragging} = this.state
        if (!dragging || !this.containerRef.current) return

        let containerRect = this.containerRef!.current.getBoundingClientRect()

        if (containerRect) {
            let {minimumWidth=DEFAULT_VALS.minimumWidth} = this.props
            let containerWidth = Math.floor(containerRect.width)
            let calculatedWidthLeft = e.clientX - containerRect.left

            if (calculatedWidthLeft < minimumWidth) {
                calculatedWidthLeft = minimumWidth
            }
            else if (calculatedWidthLeft > (containerWidth - minimumWidth)) {
                calculatedWidthLeft = containerWidth - minimumWidth
            }

            let calculatedWidthRight = containerWidth - calculatedWidthLeft

            this.setState({ widthLeft: `${calculatedWidthLeft}px`, widthRight: `${calculatedWidthRight}px` })
        }
    }

    render()  {
        let {widthLeft, widthRight, dragging} = this.state
        let { panelLeft, panelRight,
            allowResize=DEFAULT_VALS.allowResize,
            dividerWidth=DEFAULT_VALS.dividerWidth,
            classNameContainer=DEFAULT_VALS.classNameContainer,
            classNameLeftPanel=DEFAULT_VALS.classNameLeftPanel, 
            classNameRightPanel=DEFAULT_VALS.classNameRightPanel,
            classNameDivider=DEFAULT_VALS.classNameDivider} = this.props

        return (
            <div ref={this.containerRef} className={`split-panel-container ${dragging ? "no-select": ""} ${classNameContainer}`}>
                <div className={`split-panel-left ${classNameLeftPanel}`} style={{width: widthLeft}}>
                    {panelLeft}
                </div>

                { allowResize ?
                    <div className={`split-panel-divider ${classNameDivider}`}
                        onMouseDown={this.handleMouseDown}
                        style={{cursor: "col-resize", width: `${dividerWidth}px`}}
                    />
                    :
                    <div className={`split-panel-divider ${classNameDivider}`}
                        style={{width: `${dividerWidth}px`}}
                    />
                }

                <div className={`split-panel-right ${classNameRightPanel}`} style={{width: widthRight}}>
                    {panelRight}
                </div>
            </div>
        )
    }
}
